/**
 * CookieConsent - Portal wrapper around lib's CookieConsent.
 *
 * Wires i18n labels and preserves the legacy storage key
 * (`ecgrid.cookieConsent.v1`) so existing accepted users do not see the
 * banner again after migration.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CookieConsent as LibCookieConsent } from '@/components/ui';
import { NS } from '@/i18n/namespaces';

const STORAGE_KEY = 'ecgrid.cookieConsent.v1';

export interface CookieConsentProps {
  /** External Privacy Policy link (defaults to ld.com) */
  privacyHref?: string;
  className?: string;
  /** Hook for analytics opt-in flow */
  onAccept?: () => void;
  /** Hook for decline flow */
  onDecline?: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({
  privacyHref = 'https://www.ld.com/privacy/',
  className,
  onAccept,
  onDecline,
}) => {
  const { t } = useTranslation(NS.COMMON);
  return (
    <LibCookieConsent
      title={t(
        'CookieConsent_Headline',
        "You must 'Accept' our cookie policy to continue your action."
      )}
      message={t(
        'CookieConsent_Body',
        'We use cookies necessary for the functioning of our website.'
      )}
      acceptLabel={t('CookieConsent_Accept', 'Accept')}
      declineLabel={t('CookieConsent_Decline', 'Decline')}
      privacyHref={privacyHref}
      privacyLinkLabel={t('Footer_PrivacyPolicy', 'Privacy Policy')}
      storageKey={STORAGE_KEY}
      onAccept={onAccept}
      onDecline={onDecline}
      className={className}
    />
  );
};

CookieConsent.displayName = 'CookieConsent';

export const COOKIE_CONSENT_STORAGE_KEY = STORAGE_KEY;

export default CookieConsent;
